/**
 * Pure status-badge renderers shared by the Deliveries table, the Submitted
 * Appeals sub-tab and the delivery rows. Extracted from DeliveryTab.tsx to keep
 * that component under the 500-line limit. No component state — safe to import
 * anywhere a badge is rendered.
 */
import type { ReactNode } from 'react';
import { ON_TIME_STATUS_LABELS } from '../../../../lib/onTimeStatus';
import { partialOverrideNote, type WeekAllocation } from './partialOverride';

/**
 * Appeal-status pill. Falls back to a workflow/on-time-aware "Not Appealed" /
 * "Acknowledged" label (or a blank dash for on-time deliveries with no appeal,
 * unless the quantity is wrong — that row can be appealed, so it reads "Not Appealed").
 */
export function getAppealStatusBadge(
  status: string,
  isCustomerAppeal?: boolean,
  workflowStatus?: string,
  onTimeStatus?: string,
  qtyMismatch?: boolean,
): ReactNode {
  let label = status;
  let cls = 'bg-gray-100 text-gray-500 ring-gray-200';
  // "Awaiting …"/"Query" are action-required states — flag them so they get a
  // pulsing dot that draws the eye to the row that still needs a decision.
  let awaitingAction = false;
  if (status === 'awaiting_response') {
    label = isCustomerAppeal ? 'Awaiting Supplier Response' : 'Awaiting Customer Response';
    cls = 'bg-amber-50 text-amber-800 ring-amber-300';
    awaitingAction = true;
  } else if (status === 'accepted') { label = 'Accepted'; cls = 'bg-green-50 text-green-800 ring-green-300'; }
  else if (status === 'queried') { label = 'Query – Awaiting Supplier Response'; cls = 'bg-blue-50 text-blue-800 ring-blue-300'; awaitingAction = true; }
  else if (status === 'rejected') { label = 'Rejected'; cls = 'bg-red-50 text-red-800 ring-red-300'; }
  else if (status === 'cancelled') { label = 'Appeal Cancelled'; cls = 'bg-gray-100 text-gray-600 ring-gray-300'; }
  else if (status === 'none' || !status) {
    if (workflowStatus === 'acknowledged') {
      label = 'Acknowledged';
      cls = 'bg-green-50 text-green-800 ring-green-300';
    } else if (onTimeStatus === 'on_time' && !qtyMismatch) {
      // CR-4651 AC #1: On-Time deliveries have no appeal action → blank status
      return <span className="text-gray-400">-</span>;
    } else {
      label = 'Not Appealed';
    }
  }
  // Colour of the pulsing dot tracks the badge tint (amber for awaiting, blue
  // for queried). Only shown for action-required states.
  const dotColor = status === 'queried' ? 'bg-blue-500' : 'bg-amber-500';
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${cls}`}>
      {awaitingAction && (
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${dotColor}`} />
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${dotColor}`} />
        </span>
      )}
      {label}
    </span>
  );
}

/**
 * On-time-status pill (On Time / Early / Late).
 *
 * CR-6517: once a line's OTIF is overridden, an Early/Late delivery is
 * resolved — render "Overridden (Early/Late)" in amber so it doesn't read as
 * still needing action. The backend sends the finished label in
 * `on_time_status_display`; use it when present so the wording lives in one
 * place, and fall back to composing it locally.
 *
 * @param status - raw on_time_status from the GRN
 * CR-6518: a delivery that consolidates several scheduled weeks only reads
 * "Overridden (…)" once EVERY week it covers is settled. While some are, the
 * verdict correctly still says Late — so pass `allocations` and the badge adds
 * "1 of 4 weeks overridden" beneath it. Without that the buyer sees an
 * unchanged Late and no evidence their override landed.
 *
 * @param status - raw on_time_status from the GRN
 * @param overridden - GRN.otif_overridden (line- or schedule-level)
 * @param displayLabel - GRN.on_time_status_display from the backend
 * @param allocations - GRN.allocations, for the partial-override note
 */
export function getOntimeStatusBadge(
  status: string,
  /** Decided in OTIF review -- overridden, or acknowledged (EXPEDITING-3): the
   *  resolved styling; the label (displayLabel) says which. */
  overridden?: boolean,
  displayLabel?: string,
  allocations?: readonly WeekAllocation[] | null,
): ReactNode {
  // CR-6518: an overridden delivery scores as a pass, so it reads green — but
  // it names the verdict it replaced, so an accepted exception stays
  // distinguishable from a delivery that met its date on its own merits.
  // CR-7418: the LABELS come from lib/onTimeStatus, which owns the vocabulary;
  // this map only carries what is local to a badge, which is the colour.
  const CLASSES: Record<string, string> = {
    on_time: 'bg-green-100 text-green-800',
    early: 'bg-blue-100 text-blue-800',
    late: 'bg-red-100 text-red-800',
    overridden_early: 'bg-green-100 text-green-800',
    overridden_late: 'bg-green-100 text-green-800',
  };
  const statusMap: Record<string, { label: string; class: string }> = Object.fromEntries(
    Object.entries(CLASSES).map(([key, cls]) => [key, { label: ON_TIME_STATUS_LABELS[key], class: cls }]),
  );
  const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
  // Prefer the backend-computed label (which already carries the "Overridden (…)"
  // prefix) so the wording stays in one place; otif_overridden only decides the
  // amber "resolved" styling.
  const label = displayLabel || (
    overridden && (status === 'early' || status === 'late')
      ? `Overridden (${config.label})`
      : config.label
  );
  const cls = overridden && (status === 'early' || status === 'late')
    ? 'bg-amber-100 text-amber-800'
    : config.class;
  const partial = partialOverrideNote(allocations);
  const pill = (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
  if (!partial) return pill;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      {pill}
      <span className="text-[11px] font-medium text-amber-700 whitespace-nowrap">
        {partial}
      </span>
    </span>
  );
}

/**
 * Customer review 11 Sep (item 36): the Variance (Days) pill. The day
 * figure is receipt minus the verdict's own reference date and the wording /
 * colour follow the stored On-Time verdict — never the sign of the number —
 * so "On-Time Status: Late" can no longer sit beside "-1945 days (early)".
 * A few days either side that the supplier's tolerance window absorbs reads
 * "(on time)".
 *
 * @param days - signed days vs the verdict reference date (negative = before)
 * @param onTimeStatus - the GRN's stored on_time_status (the verdict)
 * @returns the pill, or a dash when there is no figure
 */
export function getVarianceBadge(days: number | null | undefined, onTimeStatus?: string | null): ReactNode {
  if (days === null || days === undefined) return <span className="text-base">-</span>;
  const figure = days > 0 ? `+${days}` : `${days}`;
  const unit = Math.abs(days) === 1 ? 'day' : 'days';
  const verdict: Record<string, { label: string; cls: string }> = {
    early: { label: 'early', cls: 'bg-blue-100 text-blue-800' },
    late: { label: 'late', cls: 'bg-red-100 text-red-800' },
    on_time: { label: 'on time', cls: 'bg-green-100 text-green-800' },
    overridden_early: { label: 'overridden early', cls: 'bg-emerald-100 text-emerald-800' },
    overridden_late: { label: 'overridden late', cls: 'bg-emerald-100 text-emerald-800' },
  };
  const v = onTimeStatus ? verdict[onTimeStatus] : undefined;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${v?.cls ?? 'bg-gray-100 text-gray-700'}`}>
      {figure} {unit}{v ? ` (${v.label})` : ''}
    </span>
  );
}
